<template>
  <LandingBackground>
    <div class="mb-20">
      <div class="mx-auto mt-10 grid grid-cols-1 items-start gap-10 sm:px-5 md:px-8 xl:grid-cols-2 3xl:grid-cols-3">
        <div class="flex min-w-0 flex-col gap-10">
          <LandingCard variant="info" :title="`Welcome, ${userRef.firstName} ${userRef.lastName}`">
            <template #default>
              <LandingCardLinkSections label="Emergency Response Incidents">
                <LandingCardLinkSection
                  link-label="Find Emergency Response Incidents"
                  description="Find, view, and manage hazardous substance spill and emergency response."
                  :to="{ name: routeNames.incidents.incidents }"
                />
              </LandingCardLinkSections>
              <LandingCardLinkSections label="HEPCRA Facilities">
                <LandingCardLinkSection
                  link-label="Find HEPCRA Facilities"
                  description="Find, view, and manage facilities required to report hazardous and toxic chemicals."
                  :to="{ name: routeNames.facilities.facilities }"
                />
              </LandingCardLinkSections>
              <LandingCardLinkSections label="Discovery and Remediation Sites">
                <LandingCardLinkSection
                  link-label="Find Discovery and Remediation Sites"
                  description="Find, view, and manage site identification, investigation, and remediation activities."
                  :to="{ name: routeNames.sites.sites }"
                />
              </LandingCardLinkSections>
              <Authorize permission-name="ReadInquiry">
                <LandingCardLinkSections label="Hazard Evaluation Inquiries">
                  <LandingCardLinkSection
                    link-label="Find Hazard Evaluation Inquiries"
                    description="Find, view, and manage reported pesticide and heavy metal poisoning."
                    :to="{ name: routeNames.inquiries.inquiries }"
                  />
                </LandingCardLinkSections>
                <template #pending>
                  <LandingCardSkeleton class="py-6" />
                </template>
              </Authorize>
              <LandingCardLinkSections label="Analytics">
                <LandingCardLinkSection
                  link-label="Download Incident Data"
                  :to="{ name: routeNames.analytics.incidentDataDownload }"
                />
                <LandingCardLinkSection
                  link-label="Export BC/States Data"
                  :to="{ name: routeNames.analytics.bcStatesExport }"
                />
                <LandingCardLinkSection
                  link-label="Export Release Notification Log Data"
                  :to="{ name: routeNames.analytics.releaseNotificationLog }"
                />
                <LandingCardLinkSection
                  link-label="Export Release Notification Log with Quantity Data"
                  :to="{ name: routeNames.analytics.releaseNotificationLogWithQuantity }"
                />
              </LandingCardLinkSections>
            </template>
          </LandingCard>
        </div>
        <div class="flex min-w-0 flex-col gap-10 xl:col-start-2 3xl:contents">
          <div class="flex min-w-0 flex-col gap-10 3xl:col-start-2">
            <LandingCard
              title="Emergency Response Incidents"
              :view-all-to="{ name: routeNames.incidents.incidents, query: { statusMurnIn: [] } }"
            >
              <LandingCardSkeleton v-if="areIncidentsLoading" />
              <div class="bg-neutral-50 p-10 sm:rounded-b-lg" v-if="!areIncidentsLoading && !openIncidents?.length">
                <NoneFoundFlag label="No Open Incidents" />
              </div>

              <LandingCardEntityList
                title="Open &ndash; 5 newest"
                :items="openIncidents"
                :view-all-to="{
                  name: routeNames.incidents.incidents,
                  query: { statusMurnIn: [LE.IncidentStatus.Open] },
                }"
              >
                <template #default="{ item }">
                  <LandingCardEntityListItem>
                    <template #primaryLabel> Case No. {{ item.caseNumber }} </template>
                    <template #secondaryLabel>
                      {{ item.releaseName }}
                    </template>
                    <template #additionalInfo>
                      <div
                        class="mt-1.5 text-sm text-neutral-400 md:mt-0 md:self-center md:pr-4 md:text-right"
                        v-if="item.assignedSoscMurn"
                      >
                        <span class="md:block">
                          Assigned SOSC -
                          <span class="text-neutral-500">
                            <LookupDescription :murn="item.assignedSoscMurn" />
                          </span>
                        </span>
                      </div>
                    </template>
                  </LandingCardEntityListItem>
                </template>
              </LandingCardEntityList>
            </LandingCard>

            <LandingCard
              title="HEPCRA Facilities"
              :view-all-to="{ name: routeNames.facilities.facilities, query: { statusMurnIn: [] } }"
            >
              <template #default>
                <div class="py-5 pl-4 sm:pl-6">
                  <PublicLandingList>
                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.facilities.facilities,
                          query: { statusMurnIn: [LE.FacilityStatus.CourtesyFiling] },
                        }"
                      >
                        View Courtesy Filing Facilities</router-link
                      >
                    </PublicLandingListItem>

                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.facilities.facilities,
                          query: { statusMurnIn: [LE.FacilityStatus.MasterorCurrentlyOperating] },
                        }"
                      >
                        View Master or Currently Operating Facilities</router-link
                      >
                    </PublicLandingListItem>

                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.facilities.facilities,
                          query: { statusMurnIn: [LE.FacilityStatus.FormROnly] },
                        }"
                      >
                        View Form R Only Facilities</router-link
                      >
                    </PublicLandingListItem>

                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.facilities.facilities,
                          query: { statusMurnIn: [LE.FacilityStatus.NeedsInspectiontoDetermineStatus] },
                        }"
                      >
                        View Facilities Needing Inspection To Determine Status</router-link
                      >
                    </PublicLandingListItem>

                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.facilities.facilities,
                          query: { statusMurnIn: [LE.FacilityStatus.ServiceStation] },
                        }"
                      >
                        View Service Station Facilities</router-link
                      >
                    </PublicLandingListItem>
                  </PublicLandingList>
                </div>
              </template>
            </LandingCard>
          </div>

          <div class="flex min-w-0 flex-col gap-10 3xl:col-start-3">
            <LandingCard
              title="Discovery and Remediation Sites"
              :view-all-to="{ name: routeNames.sites.sites, query: { assignedRpmMurnIn: [], priorityMurnIn: [] } }"
            >
              <template #default>
                <div class="py-5 pl-4 sm:pl-6">
                  <PublicLandingList>
                    <PublicLandingListItem v-if="assignedSiteUserMurnRef">
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.sites.sites,
                          query: { assignedRpmMurnIn: [assignedSiteUserMurnRef] },
                        }"
                      >
                        View My Assigned Sites</router-link
                      >
                    </PublicLandingListItem>

                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.sites.sites,
                          query: { assignedRpmMurnIn: [LE.RemedialProjectManager.Unassigned] },
                        }"
                      >
                        View Unassigned Sites</router-link
                      >
                    </PublicLandingListItem>
                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.sites.sites,
                          query: { priorityMurnIn: [LE.SitePriority.High] },
                        }"
                      >
                        View High Priority Sites</router-link
                      >
                    </PublicLandingListItem>
                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.sites.sites,
                          query: { priorityMurnIn: [LE.SitePriority.Unranked] },
                        }"
                      >
                        View Unranked Sites</router-link
                      >
                    </PublicLandingListItem>
                    <PublicLandingListItem>
                      <router-link
                        class="text-sky-600 hover:underline"
                        :to="{
                          name: routeNames.sites.sites,
                          query: { isPublic: 'false' },
                        }"
                      >
                        View Not For Public Sites</router-link
                      >
                    </PublicLandingListItem>
                  </PublicLandingList>
                </div>
              </template>
            </LandingCard>

            <Authorize permission-name="ReadInquiry">
              <LandingCard
                title="Hazard Evaluation Inquiries"
                :view-all-to="{ name: routeNames.inquiries.inquiries, query: { statusMurnIn: [] } }"
              >
                <LandingCardSkeleton v-if="areInquiriesLoading" />
                <div class="bg-neutral-50 p-10 sm:rounded-b-lg" v-if="!areInquiriesLoading && !openInquiries?.length">
                  <NoneFoundFlag label="No Open Hazard Evaluation Inquiries" />
                </div>

                <LandingCardEntityList
                  title="Open &ndash; 5 newest"
                  :items="openInquiries"
                  :view-all-to="{
                    name: routeNames.inquiries.inquiries,
                    query: { statusMurnIn: [LE.InquiryStatus.Open] },
                  }"
                >
                  <template #default="{ item }">
                    <LandingCardEntityListItem>
                      <template #primaryLabel> HEIR Data ID {{ item.id }} </template>
                      <template #secondaryLabel> Source - <LookupDescription :murn="item.sourceMurn" /> </template>
                      <template #additionalInfo>
                        <div
                          class="mt-1.5 text-sm text-neutral-400 md:mt-0 md:self-center md:pr-4 md:text-right"
                          v-if="item.contactDate"
                        >
                          <span class="md:block"
                            >Date of Contact
                            <span class="text-neutral-500">{{ sysDate.shortDateString(item.contactDate) }}</span></span
                          >
                        </div>
                      </template>
                    </LandingCardEntityListItem>
                  </template>
                </LandingCardEntityList>
              </LandingCard>

              <template #pending>
                <SkeletonBlock class="h-40 w-full" />
              </template>
            </Authorize>
          </div>
        </div>
      </div>
    </div>
  </LandingBackground>
</template>

<script lang="ts">
import LandingCard from "@/components/landing/LandingCard.vue";
import LandingCardEntityList from "@/components/landing/LandingCardEntityList.vue";
import LandingCardEntityListItem from "@/components/landing/LandingCardEntityListItem.vue";
import LandingCardLinkSection from "@/components/landing/LandingCardLinkSection.vue";
import LandingCardLinkSections from "@/components/landing/LandingCardLinkSections.vue";
import LandingCardSkeleton from "@/components/landing/LandingCardSkeleton.vue";
import LandingBackground from "@/components/LandingBackground.vue";
import { LE } from "@/constants/enums";
import { routeNames } from "@/routes";
import type { Incident } from "@/views/incident/incident";
import { getIncidents } from "@/views/incident/incidentApi";
import incidentService from "@/views/incident/incidentService";
import type { Inquiry } from "@/views/inquiry/inquiry";
import { getInquiries } from "@/views/inquiry/inquiryApi";
import inquiryService from "@/views/inquiry/inquiryService";
import { BaseButton } from "@modernary/mui/components/button";
import { NoneFoundFlag, SkeletonBlock, useAppSettingsStore } from "@modernary/mui/components/common";
import { adaptPagedResponse, getLookupsFromLookupGroup, LookupDescription } from "@modernary/mui/components/lookup";
import { PublicLandingList, PublicLandingListItem } from "@modernary/mui/components/publicLanding";
import { useToastStore } from "@modernary/mui/components/toasts";
import type { Paging } from "@modernary/mui/utils/api";
import { useUser } from "@modernary/mui/utils/authentication";
import { LogoutButton } from "@modernary/mui/utils/authentication/buttons";
import { Authorize } from "@modernary/mui/utils/authorization";
import { sysDate, tzDate } from "@modernary/mui/utils/filters";
import { AxiosError } from "axios";
import { defineComponent, ref } from "vue";
import type { LandingCardEntity } from "./landing/landingCard";
import { hide403s } from "@modernary/mui/utils/helpers";

export default defineComponent({
  name: "HomeInternal",
  components: {
    Authorize,
    LandingBackground,
    LandingCard,
    LandingCardLinkSection,
    LandingCardLinkSections,
    LandingCardEntityList,
    LandingCardEntityListItem,
    LandingCardSkeleton,
    LogoutButton,
    PublicLandingList,
    PublicLandingListItem,
    NoneFoundFlag,
    SkeletonBlock,
    LookupDescription,
  },
  setup() {
    const { userRef } = useUser();
    const appSettingsStore = useAppSettingsStore();

    const toastStore = useToastStore();
    const areIncidentsLoading = ref(1);
    const areInquiriesLoading = ref(1);

    const assignedSiteUserMurnRef = ref<string | null>(null);

    getLookupsFromLookupGroup("RemedialProjectManager").then((result) => {
      result._items.forEach((lookup) => {
        if (lookup.longDescription === userRef.value.email) {
          assignedSiteUserMurnRef.value = lookup.murn;
        }
      });
    });

    const openIncidents = ref<LandingCardEntity<Incident>[]>();
    getIncidents(
      {
        statusMurnIn: [LE.IncidentStatus.Open],
        orderBy: [{ propertyName: "reportedDateTime", isDescending: true }],
        take: 5,
      },
      hide403s(),
    )
      .then(async (response) => {
        const data = (await adaptPagedResponse(response, incidentService.responseAdapter as any)) as unknown as Paging<
          LandingCardEntity<Incident>
        >;
        data._items.forEach((item) => {
          item.to = { name: routeNames.incidents.view, params: { id: item.id } };
        });
        openIncidents.value = data._items;
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          toastStore.toastError((options) => {
            options.title = "Could not get Incidents";
          }, error.status || 500);
        }
      })
      .finally(() => {
        areIncidentsLoading.value--;
      });

    const openInquiries = ref<LandingCardEntity<Inquiry>[]>();
    getInquiries(
      {
        statusMurnIn: [LE.InquiryStatus.Open],
        orderBy: [{ propertyName: "contactDate", isDescending: true }],
        take: 5,
      },
      hide403s(),
    )
      .then(async (response) => {
        const data = (await adaptPagedResponse(response, inquiryService.responseAdapter as any)) as unknown as Paging<
          LandingCardEntity<Inquiry>
        >;
        data._items.forEach((item) => {
          item.to = { name: routeNames.inquiries.view, params: { id: item.id } };
        });
        openInquiries.value = data._items;
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          toastStore.toastError((options) => {
            options.title = "Could not get Inquiries";
          }, error.status || 500);
        }
      })
      .finally(() => {
        areInquiriesLoading.value--;
      });

    return {
      userRef,
      routeNames,
      BaseButton,
      appSettingsStore,
      LE,
      openIncidents,
      tzDate,
      areIncidentsLoading,
      openInquiries,
      sysDate,
      assignedSiteUserMurnRef,
      areInquiriesLoading,
    };
  },
});
</script>
